import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import {
  Layers, Loader2, Sparkles, CheckCircle2, AlertTriangle, MapPin,
  Package, ChevronDown, ChevronUp, RefreshCw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AssemblyExtractor({ projectId, takeoff, projectStatus, onExtracted }) {
  const [extracting, setExtracting] = useState(false);
  const [assemblies, setAssemblies] = useState([]);
  const [loadingAssemblies, setLoadingAssemblies] = useState(true);
  const [expandedCodes, setExpandedCodes] = useState({});

  // Load assemblies from DB (auto-extracted during takeoff)
  const fetchAssemblies = useCallback(async () => {
    try {
      const res = await fetch(`${API}/projects/${projectId}/assemblies`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAssemblies(data || []);
      }
    } catch {}
    finally { setLoadingAssemblies(false); }
  }, [projectId]);

  useEffect(() => { fetchAssemblies(); }, [fetchAssemblies]);

  const manualExtract = async () => {
    setExtracting(true);
    try {
      const res = await fetch(`${API}/projects/${projectId}/extract-assemblies`, {
        method: 'POST', credentials: 'include'
      });
      if (res.ok) {
        await fetchAssemblies();
        onExtracted?.();
      }
    } catch {}
    finally { setExtracting(false); }
  };

  const toggleExpand = (code) => {
    setExpandedCodes(prev => ({ ...prev, [code]: !prev[code] }));
  };

  // Get assembly data from takeoff result (auto-extracted)
  const takeoffSchedules = takeoff?.assembly_schedules || [];
  const takeoffInstances = takeoff?.assembly_instances || [];
  const takeoffTotals = takeoff?.assembly_totals || {};
  const hasAutoExtracted = takeoffSchedules.length > 0 || assemblies.length > 0;

  if (loadingAssemblies) {
    return (
      <Card className="border-[#E2E8ED] shadow-none">
        <CardContent className="py-8 text-center">
          <Loader2 className="w-6 h-6 text-[#1D4053] animate-spin mx-auto mb-2" />
          <p className="text-xs text-[#72A4BF]">Loading assemblies...</p>
        </CardContent>
      </Card>
    );
  }

  if (projectStatus === 'analyzing') {
    return (
      <Card className="border-[#E2E8ED] shadow-none">
        <CardContent className="py-8 text-center">
          <Loader2 className="w-8 h-8 text-[#1D4053] animate-spin mx-auto mb-3" />
          <h3 className="text-sm font-semibold text-[#0D1830] mb-1">Analyzing Documents</h3>
          <p className="text-xs text-[#72A4BF]">
            Assemblies will be extracted automatically as part of the takeoff analysis.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!hasAutoExtracted) {
    return (
      <Card className="border-[#E2E8ED] shadow-none" data-testid="assembly-extractor">
        <CardContent className="py-8 text-center">
          <Layers className="w-12 h-12 text-[#1D4053]/30 mx-auto mb-4" />
          <h3 className="text-sm font-semibold text-[#0D1830] mb-2">No Assemblies Found</h3>
          <p className="text-xs text-[#72A4BF] mb-4 max-w-md mx-auto">
            {projectStatus === 'completed'
              ? 'No assembly codes were detected during the takeoff analysis. You can try extracting them manually.'
              : 'Run the takeoff analysis first — assemblies will be extracted automatically.'}
          </p>
          {projectStatus === 'completed' && (
            <Button
              onClick={manualExtract}
              disabled={extracting}
              variant="outline"
              className="rounded-full px-6 border-[#1D4053]/30"
              data-testid="extract-assemblies-btn"
            >
              {extracting ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" />Extracting...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" />Extract Assembly Codes</>
              )}
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  const scheduleData = takeoffSchedules.length > 0 ? takeoffSchedules : assemblies.map(a => ({
    assembly_code: a.assembly_name,
    assembly_type: a.assembly_type,
    description: a.description,
    components: a.materials || [],
    source_page: a.source_page,
  }));

  return (
    <div className="space-y-4" data-testid="assembly-results">
      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <Card className="border-[#E2E8ED] shadow-none">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#72A4BF]/10 flex items-center justify-center">
              <Layers className="w-5 h-5 text-[#0D1830]" />
            </div>
            <div>
              <p className="text-xl font-bold text-[#0D1830]">{scheduleData.length}</p>
              <p className="text-xs text-[#72A4BF]">Assemblies Found</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E2E8ED] shadow-none">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#E8F5E9] flex items-center justify-center">
              <MapPin className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-[#0D1830]">{takeoffInstances.length}</p>
              <p className="text-xs text-[#72A4BF]">Instances Located</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E2E8ED] shadow-none">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#E3F2FD] flex items-center justify-center">
              <Package className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-[#0D1830]">{Object.keys(takeoffTotals).length || scheduleData.length}</p>
              <p className="text-xs text-[#72A4BF]">Unique Codes</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
          <span className="text-xs text-[#72A4BF]">Auto-extracted during takeoff analysis</span>
        </div>
        <Button
          onClick={manualExtract}
          disabled={extracting}
          variant="ghost"
          size="sm"
          className="text-xs text-[#72A4BF] hover:text-[#0D1830]"
          data-testid="re-extract-assemblies-btn"
        >
          {extracting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
          Re-extract
        </Button>
      </div>

      {/* Assembly Schedule Table */}
      {scheduleData.map((sched, i) => {
        const code = sched.assembly_code || `Assembly ${i + 1}`;
        const isExpanded = expandedCodes[code];
        const totalForCode = takeoffTotals[code];
        return (
          <Card key={i} className="border-[#E2E8ED] shadow-none overflow-hidden" data-testid={`assembly-card-${i}`}>
            <CardHeader
              className="py-3 px-4 bg-[#F7FAFB] cursor-pointer hover:bg-[#EDF2F5] transition-colors"
              onClick={() => toggleExpand(code)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-[#0D1830] text-white text-xs flex items-center justify-center font-bold">
                    {code}
                  </span>
                  <div>
                    <CardTitle className="text-sm font-semibold text-[#0D1830]">
                      {sched.assembly_type || code}
                    </CardTitle>
                    {sched.description && (
                      <p className="text-[10px] text-[#72A4BF] mt-0.5">{sched.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {totalForCode && (
                    <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">
                      {typeof totalForCode === 'object' ? `${totalForCode.total_quantity} ${totalForCode.unit || ''}` : totalForCode} instances
                    </Badge>
                  )}
                  {sched.source_page && (
                    <Badge className="bg-[#72A4BF]/10 text-[#0D1830] border-0 text-[10px]">
                      {sched.source_page}
                    </Badge>
                  )}
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-[#72A4BF]" /> : <ChevronDown className="w-4 h-4 text-[#72A4BF]" />}
                </div>
              </div>
            </CardHeader>
            {isExpanded && sched.components?.length > 0 && (
              <CardContent className="pt-2 pb-3">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[#E2E8ED]">
                      <TableHead className="text-xs">Material / Component</TableHead>
                      <TableHead className="text-xs text-right">Qty</TableHead>
                      <TableHead className="text-xs text-right">Unit</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sched.components.map((comp, ci) => (
                      <TableRow key={ci} className="border-[#F0F4F7]">
                        <TableCell className="text-xs">{comp.name || comp.component_name || comp.material || '-'}</TableCell>
                        <TableCell className="text-xs text-right">{comp.quantity || '-'}</TableCell>
                        <TableCell className="text-xs text-right text-[#72A4BF]">{comp.unit || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
}
